import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { grnApiService, GRN, GRNDetail } from '../services/grnApi';
import { rawItemApiService, RawItem } from '../services/rawItemApi';
import { encodeRmQrPayload } from '../utils/rmQrPayload';
import { RmQrLabelSticker } from './RmQrLabelSticker';

export interface RmLabelPrintItem {
  key: string;
  itemName: string;
  size: string;
  grade: string;
  lotNo: string;
  supplier: string;
  qrValue: string;
  nopIndex: number;
  nopTotal: number;
}

function resolveNopTotal(nop?: number | null): number {
  const n = Number(nop);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

function supplierLabel(grn: GRN | null, _detail: GRNDetail): string {
  const s = grn?.supplier;
  if (s?.supplierName?.trim()) return s.supplierName.trim();
  if (s?.supplierCode?.trim()) return s.supplierCode.trim();
  return '—';
}

/** Expand each GRN detail into one sticker per NOP. */
export function buildRmLabelsFromGrnDetails(
  grn: GRN,
  details: GRNDetail[],
  detailIdFilter?: number | null,
): RmLabelPrintItem[] {
  const lines = details.filter((d) => {
    if (detailIdFilter != null && d.id !== detailIdFilter) return false;
    return !!(d.heatNo || d.rawItem?.sku);
  });

  const items: RmLabelPrintItem[] = [];
  for (const d of lines) {
    const sku = (d.rawItem?.sku || '').trim();
    const heat = (d.heatNo || '').trim();
    const nopTotal = resolveNopTotal(d.nop);
    const qrValue = encodeRmQrPayload(sku, heat || null);
    const itemName = (d.rawItem?.itemName || '').trim() || '—';
    const size = (d.rawItem?.size || '').trim() || '—';
    const grade = (d.rawItem?.grade || '').trim() || '—';
    const lotNo = heat || (d.supplierHeatNo || '').trim() || '—';
    const supplier = supplierLabel(grn, d);

    for (let i = 1; i <= nopTotal; i++) {
      items.push({
        key: `${d.id}-${i}`,
        itemName,
        size,
        grade,
        lotNo,
        supplier,
        qrValue,
        nopIndex: i,
        nopTotal,
      });
    }
  }
  return items;
}

/**
 * A4 print sheet of RM stickers for a GRN (one sticker per NOP; same QR per heat).
 * Route: /grn/view/:id/labels?detailId=optional
 */
export function RmGrnLabelsPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const detailIdParam = searchParams.get('detailId');
  const detailIdFilter =
    detailIdParam != null && detailIdParam !== '' && !Number.isNaN(Number(detailIdParam))
      ? Number(detailIdParam)
      : null;

  const [loading, setLoading] = useState(true);
  const [grn, setGrn] = useState<GRN | null>(null);
  const [details, setDetails] = useState<GRNDetail[]>([]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [grnData, detailsData] = await Promise.all([
          grnApiService.getGRNById(id),
          grnApiService.getGRNDetailsByGRN(parseInt(id, 10)),
        ]);
        if (cancelled) return;
        setGrn(grnData);

        let lines = Array.isArray(detailsData) ? detailsData : [];
        // GRN-detail rawItem is often a slim projection (id/name/sku only) — size/grade missing.
        // Enrich from full raw-item master when needed.
        const needsEnrich = lines.filter(
          (d) =>
            d.rawItem?.id &&
            (!(d.rawItem.size || '').trim() || !(d.rawItem.grade || '').trim()),
        );
        if (needsEnrich.length > 0) {
          const uniqueIds = Array.from(new Set(needsEnrich.map((d) => d.rawItem!.id)));
          const fetched = await Promise.all(
            uniqueIds.map(async (rawId) => {
              try {
                const full = await rawItemApiService.getRawItemById(String(rawId));
                return [rawId, full] as const;
              } catch {
                return [rawId, null] as const;
              }
            }),
          );
          const byId = new Map<number, RawItem>();
          for (const [rawId, full] of fetched) {
            if (full) byId.set(rawId, full);
          }
          lines = lines.map((d) => {
            const full = d.rawItem?.id != null ? byId.get(d.rawItem.id) : undefined;
            if (!full) return d;
            return {
              ...d,
              rawItem: {
                ...d.rawItem,
                ...full,
                size: (full.size || d.rawItem.size || '').trim(),
                grade: (full.grade || d.rawItem.grade || '').trim(),
                sku: full.sku || d.rawItem.sku,
              },
            };
          });
        }

        if (cancelled) return;
        setDetails(lines);
      } catch {
        toast.error('Failed to load GRN for labels');
        navigate(`/grn/view/${id}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const labels = useMemo(() => {
    if (!grn) return [];
    return buildRmLabelsFromGrnDetails(grn, details, detailIdFilter);
  }, [grn, details, detailIdFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="rm-labels-print-root bg-gray-100 min-h-screen">
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/grn/view/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to GRN
        </Button>
        <div className="text-sm text-gray-600">
          {grn?.grnNumber ? `${grn.grnNumber} · ` : ''}
          {labels.length} sticker{labels.length === 1 ? '' : 's'}
          {detailIdFilter != null ? ' (selected line)' : ''}
        </div>
        <Button
          onClick={() => window.print()}
          disabled={labels.length === 0}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Printer className="h-4 w-4 mr-2" />
          Print A4
        </Button>
      </div>

      {labels.length === 0 ? (
        <div className="p-8 text-center text-gray-600 print:hidden">
          No stickers to print. Ensure the line has a heat / SKU.
        </div>
      ) : (
        <div className="rm-labels-a4-sheet">
          <div className="rm-labels-grid">
            {labels.map((item) => (
              <RmQrLabelSticker
                key={item.key}
                itemName={item.itemName}
                size={item.size}
                grade={item.grade}
                lotNo={item.lotNo}
                supplier={item.supplier}
                qrValue={item.qrValue}
                nopIndex={item.nopIndex}
                nopTotal={item.nopTotal}
              />
            ))}
          </div>
        </div>
      )}

      <style>{`
        .rm-sticker {
          background: #fff;
          border: 2px solid #111;
          border-radius: 2px;
          width: 100%;
          height: 42mm;
          min-height: 42mm;
          max-height: 42mm;
          box-sizing: border-box;
          overflow: hidden;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .rm-sticker-inner {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 2mm;
          padding: 2mm 2.5mm;
          height: 100%;
          box-sizing: border-box;
        }
        .rm-sticker-fields {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 1mm;
          font-family: Arial, Helvetica, sans-serif;
          color: #111;
        }
        .rm-sticker-row {
          font-size: 9px;
          font-weight: 700;
          line-height: 1.15;
          text-transform: uppercase;
          word-break: break-word;
        }
        .rm-sticker-row:first-child .rm-sticker-value {
          text-transform: none;
        }
        .rm-sticker-label {
          white-space: nowrap;
        }
        .rm-sticker-qr-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.8mm;
          flex-shrink: 0;
        }
        .rm-sticker-qr {
          background: #fff;
          padding: 0;
          line-height: 0;
        }
        .rm-sticker-qr svg {
          width: 32mm !important;
          height: 32mm !important;
          display: block;
        }
        .rm-sticker-nop {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11px;
          font-weight: 800;
          color: #111;
          line-height: 1;
        }
        .rm-labels-a4-sheet {
          width: 210mm;
          min-height: 297mm;
          margin: 12px auto;
          padding: 8mm;
          background: #fff;
          box-sizing: border-box;
        }
        .rm-labels-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 5mm;
        }
        @media print {
          @page {
            size: A4;
            margin: 6mm;
          }
          html, body {
            background: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .rm-labels-print-root {
            background: #fff !important;
            min-height: 0;
          }
          .rm-labels-a4-sheet {
            width: auto;
            min-height: 0;
            margin: 0;
            padding: 0;
            box-shadow: none;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
